import React from 'react'
import clsx from 'clsx'
import Link from 'next/link'

import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'

import { GATrackEvent } from 'utils/googleAnalytics'
import { toKebabCase } from 'utils/text'
import Checkmark from '../../components/CheckmarkNoFill'

import type { ActivityItemProps } from 'types/microlearning'

const ActivityItem = ({
  type,
  name,
  href,
  isComplete = false,
  isShouldRetry = false,
  trackingParams = {},
  isActive = false,
}: ActivityItemProps) => {
  return (
    <div
      className={clsx(
        'flex items-center w-full mb-3 last:mb-0',
        isActive && 'bg-[#0B5FEF] p-2 rounded-lg'
      )}
    >
      <div
        className={clsx(
          'relative flex-shrink-0 flex-grow-0 flex items-center justify-center w-6 h-4 rounded-full z-10',
          !isActive && 'bg-white '
        )}
      >
        <Checkmark
          width={17}
          height={16}
          size="sm"
          className={clsx(
            !isComplete && 'text-icon-disabled',
            isComplete && !isShouldRetry && 'text-success',
            isComplete && isShouldRetry && 'text-warning',
            isActive && 'text-white'
          )}
          data-testid={
            !isComplete
              ? 'activity_not_completed'
              : !isShouldRetry
              ? 'activity_completed'
              : 'activity_should_retry'
          }
        />
      </div>
      <div className="flex items-center ml-4 flex-grow-0 flex-shrink-0">
        {type === 'text' && (
          <Icon
            data-testid="materi_text"
            as="i"
            color={isActive ? 'inverse' : 'default'}
            fontSize="small"
          >
            text_snippet
          </Icon>
        )}
        {type === 'video' && (
          <Icon
            data-testid="materi_video"
            as="i"
            color="default"
            fontSize="small"
          >
            video_library
          </Icon>
        )}
        {type === 'quiz' && (
          <Icon
            data-testid="materi_quiz"
            color={isActive ? 'inverse' : 'default'}
            as="i"
            fontSize="small"
          >
            edit
          </Icon>
        )}
      </div>
      <Text as="div" className="ml-4">
        <Link href={href} passHref>
          <a
            data-title={toKebabCase(name)}
            onClick={() => {
              GATrackEvent({
                eventName: 'learning_activity',
                eventParams: {
                  event_trigger: 'click_activity_link',
                  action_type: 'choose_activity_from_materi',
                  meta: JSON.stringify({ ...trackingParams, type } ?? {}),
                },
                sendDefaultParams: true,
              })
            }}
            href={href}
            className={clsx(
              'flex-grow font-semibold',
              isActive ? 'text-white' : 'text-interactive-default'
            )}
          >
            {name}
          </a>
        </Link>
      </Text>
    </div>
  )
}

export default ActivityItem
